import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Link as MuiLink,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Confirm, useDelete, useNotify } from 'react-admin';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { personName } from '../relationships/relationshipApi.ts';
import { getErrorMessage } from '../utils/helperUtils.ts';
import { FollowUpModal } from './FollowUpModal.tsx';
import { ImportRenewalsDialog } from './ImportRenewalsDialog.tsx';
import { RenewalPath } from './RenewalPath.tsx';
import {
  coverageLabel,
  dayOfMonth,
  DEFAULT_WINDOW,
  fetchUpcomingRenewals,
  formatDaysOut,
  formatPremium,
  formatRenewalDate,
  monthKey,
  monthLabel,
  renewalHeadline,
  rollRenewal,
  weekdayOf,
  WINDOW_CHOICES,
  type UpcomingRenewal,
  type UpcomingRenewals
} from './renewalApi.ts';

const DateTile = ({ date }: { date: string }) => (
  <Box
    sx={{
      width: 52,
      flexShrink: 0,
      textAlign: 'center',
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      py: 0.5
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
      {weekdayOf(date)}
    </Typography>
    <Typography variant="h6" sx={{ lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
      {dayOfMonth(date)}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
      {formatRenewalDate(date, false).slice(0, 3)}
    </Typography>
  </Box>
);

const RenewalLine = ({
  row,
  busy,
  onFollowUp,
  onRoll,
  onRemove
}: {
  row: UpcomingRenewal;
  busy: boolean;
  onFollowUp: (contactId: number) => void;
  onRoll: (row: UpcomingRenewal) => void;
  onRemove: (row: UpcomingRenewal) => void;
}) => {
  const when =
    row.daysOut >= 0
      ? `Renews ${formatDaysOut(row.daysOut)}`
      : `Passed ${formatDaysOut(row.daysOut)}`;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.25, flexWrap: 'wrap' }}>
      <DateTile date={row.renewalDate} />
      <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
          <MuiLink
            component={RouterLink}
            to={`/contacts/${row.contact.id}/show`}
            underline="hover"
            sx={{ fontWeight: 600 }}
          >
            {personName(row.contact)}
          </MuiLink>
          <Typography component="span" variant="body2" color="text.secondary">
            {[row.contact.company?.name, coverageLabel(row.coverage)]
              .filter(Boolean)
              .map((part) => ` · ${part}`)
              .join('')}
          </Typography>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {[when, row.incumbentCarrier, formatPremium(row.estimatedPremium)]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
        {row.opportunity ? (
          <MuiLink
            component={RouterLink}
            to={`/opportunities/${row.opportunity.id}/show`}
            underline="hover"
            variant="body2"
          >
            {row.opportunity.dealName || 'Deal'}
          </MuiLink>
        ) : null}
      </Box>
      <Box sx={{ flex: '1 1 220px' }}>
        <RenewalPath path={row.bestPath} />
      </Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{ flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end', minWidth: { md: 260 } }}
      >
        <Button size="small" variant="outlined" onClick={() => onFollowUp(row.contact.id)}>
          Log follow-up
        </Button>
        {row.daysOut < 0 ? (
          <Button size="small" variant="outlined" disabled={busy} onClick={() => onRoll(row)}>
            Roll to next year
          </Button>
        ) : null}
        {row.lapsed ? (
          <Button size="small" color="error" disabled={busy} onClick={() => onRemove(row)}>
            Remove
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
};

// The renewal calendar: every X-date inside the window, nearest first, next to
// the warmest way in. Lapsed dates stay at the top until someone deals with them.
const RenewalsPage = () => {
  const notify = useNotify();
  const [deleteOne] = useDelete();
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = Number(searchParams.get('within'));
  const within = (WINDOW_CHOICES as readonly number[]).includes(requested)
    ? requested
    : DEFAULT_WINDOW;

  const [data, setData] = useState<UpcomingRenewals | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [removing, setRemoving] = useState<UpcomingRenewal | null>(null);
  const [followUpContactId, setFollowUpContactId] = useState<number | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  useEffect(() => {
    let current = true;
    setError(null);
    fetchUpcomingRenewals(within)
      .then((result) => {
        if (current) setData(result);
      })
      .catch((err) => {
        if (current) setError(getErrorMessage(err));
      });
    return () => {
      current = false;
    };
  }, [within, reloads]);

  const { lapsed, months } = useMemo(() => {
    const all = data?.rows ?? [];
    const grouped = new Map<string, UpcomingRenewal[]>();
    for (const row of all.filter((r) => !r.lapsed)) {
      const key = monthKey(row.renewalDate);
      grouped.set(key, [...(grouped.get(key) ?? []), row]);
    }
    return { lapsed: all.filter((row) => row.lapsed), months: [...grouped] };
  }, [data]);

  const roll = async (row: UpcomingRenewal) => {
    setBusyId(row.id);
    try {
      const rolled = await rollRenewal(row.id);
      notify(`${personName(row.contact)} now renews ${formatRenewalDate(rolled.renewalDate)}`, {
        type: 'info'
      });
      reload();
    } catch (err) {
      notify(getErrorMessage(err), { type: 'warning' });
    } finally {
      setBusyId(null);
    }
  };

  const remove = () => {
    const row = removing;
    if (!row) return;
    setBusyId(row.id);
    deleteOne(
      'renewals',
      { id: row.id, previousData: row },
      {
        onSuccess: () => {
          notify('Renewal removed', { type: 'info' });
          reload();
        },
        onError: (err) => notify(getErrorMessage(err), { type: 'warning' }),
        onSettled: () => {
          setBusyId(null);
          setRemoving(null);
        }
      }
    );
  };

  const line = (row: UpcomingRenewal) => (
    <RenewalLine
      key={row.id}
      row={row}
      busy={busyId === row.id}
      onFollowUp={setFollowUpContactId}
      onRoll={roll}
      onRemove={setRemoving}
    />
  );

  return (
    <Container maxWidth="lg">
      <Breadcrumbs sx={{ mt: 2, mb: 1 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/">
          Dashboard
        </MuiLink>
        <Typography color="text.primary">Renewals</Typography>
      </Breadcrumbs>

      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          mb: 2
        }}
      >
        <Box>
          <Typography variant="h5">Renewals</Typography>
          <Typography variant="body2" color="text.secondary">
            When each account&apos;s policies come up, nearest first, with the warmest way in.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={within}
            onChange={(_event, value: number | null) => {
              if (!value) return;
              const next = new URLSearchParams(searchParams);
              if (value === DEFAULT_WINDOW) next.delete('within');
              else next.set('within', String(value));
              setSearchParams(next, { replace: true });
            }}
            aria-label="How far ahead"
          >
            {WINDOW_CHOICES.map((days) => (
              <ToggleButton key={days} value={days} sx={{ px: 1.5 }}>
                {days} days
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            startIcon={<UploadFileOutlinedIcon />}
            onClick={() => setImportOpen(true)}
          >
            Import CSV
          </Button>
        </Stack>
      </Box>

      {error ? (
        <Alert severity="error">Could not load renewals: {error}</Alert>
      ) : !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2} sx={{ pb: 4 }}>
          {data.accounts ? (
            <Typography variant="body1">{renewalHeadline(data, within)}</Typography>
          ) : null}

          {lapsed.length ? (
            <Card sx={{ borderLeft: 4, borderColor: 'warning.main' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Lapsed
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  More than 30 days past. Once you know whether the account was written or lost,
                  roll it to next year or remove it. Forge never moves a date on its own.
                </Typography>
                <Stack divider={<Divider />}>{lapsed.map(line)}</Stack>
              </CardContent>
            </Card>
          ) : null}

          {months.map(([key, monthRows]) => (
            <Card key={key}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {monthLabel(key)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {monthRows.length === 1 ? '1 renewal' : `${monthRows.length} renewals`}
                  </Typography>
                </Box>
                <Stack divider={<Divider />}>{monthRows.map(line)}</Stack>
              </CardContent>
            </Card>
          ))}

          {!data.rows.length ? (
            <Card>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Nothing renews in the next {within} days
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                  Add renewal dates on a contact&apos;s page, or import the spreadsheet you keep
                  them in.
                </Typography>
                <Stack direction="row" spacing={1.5}>
                  <Button variant="contained" onClick={() => setImportOpen(true)}>
                    Import CSV
                  </Button>
                  <Button component={RouterLink} to="/contacts">
                    Go to contacts
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ) : null}
        </Stack>
      )}

      <ImportRenewalsDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={reload}
      />
      <FollowUpModal contactId={followUpContactId} onClose={() => setFollowUpContactId(null)} />
      <Confirm
        isOpen={removing !== null}
        title="Remove this renewal?"
        content={
          removing
            ? `${personName(removing.contact)}, ${coverageLabel(removing.coverage).toLowerCase()}, ${formatRenewalDate(removing.renewalDate)}. Remove it once the account is lost or no longer worth tracking.`
            : ''
        }
        confirm="Remove"
        onConfirm={remove}
        onClose={() => setRemoving(null)}
      />
    </Container>
  );
};

export default RenewalsPage;
